import {useCallback, useRef, useState} from 'react';
import {useParams, useSearchParams} from 'react-router';
import {useTranslation} from 'react-i18next';
import PublicFormHeader from './components/PublicFormHeader';
import PublicFormInfo from './components/PublicFormInfo';
import PublicFormContent from './components/PublicFormContent';
import PublicFormNavBar from './components/PublicFormNavBar';
import {useQuery} from '@tanstack/react-query';
import {handleGetFormByIdService} from '../../services/operations/forms';
import QUERY_KEYS from '../../constants/queryKeys';
import {buildPublicFormPayload} from '../../utils';
import {uploadFiles} from '../../utils/uploadFiles';
import {useFormsMutations} from '../../hooks/useFormsHook';
import {useSnackbar} from '../../context/SnackbarContextProvider';
import {SVG} from '../../constants/assetLists';
import {getPublicFileUploadAnswerError} from './fileUploadAnswer';

/** Number of questions displayed per page */
const QUESTIONS_PER_PAGE = 4;

function PublicForm() {
  const {t} = useTranslation();

  const {formId} = useParams<{formId: string}>();
  const [searchParams] = useSearchParams();

  const user = localStorage.getItem('user');
  const parsedUser = user ? JSON.parse(user) : null;

  const responderInfo = parsedUser
    ? {
        userId: parsedUser._id,
        email: parsedUser.email,
        firstName: parsedUser.firstName,
        lastName: parsedUser.lastName,
      }
    : {
        userId: searchParams.get('userId') ?? undefined,
        email: searchParams.get('email') ?? undefined,
        firstName: searchParams.get('firstName') ?? undefined,
        lastName: searchParams.get('lastName') ?? undefined,
      };

  const {createFormResponseMutation} = useFormsMutations();
  const {showSnackbar} = useSnackbar();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);

  // Stores each question's answer keyed by question id
  const [answers, setAnswers] = useState<Record<string, any>>({});

  // Validation errors keyed by question id
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Ref to scroll the content area to top on page change
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const {data: formData} = useQuery({
    queryKey: [QUERY_KEYS.GET_FORM_BY_ID, formId],
    queryFn: () =>
      handleGetFormByIdService({
        id: formId!,
        userId: responderInfo.userId,
      }),
    enabled: !!formId,
  });

  const questions = formData?.questions ?? [];

  // ---- Pagination derived values ----
  const totalPages = Math.ceil(questions?.length / QUESTIONS_PER_PAGE);
  const isFirstPage = currentPage === 0;
  const isLastPage = currentPage >= totalPages - 1;
  const pageQuestions = questions?.slice(
    currentPage * QUESTIONS_PER_PAGE,
    (currentPage + 1) * QUESTIONS_PER_PAGE,
  );

  // Helper to check if a value is "empty" (partial input = empty)
  const isAnswerEmpty = (val: any): boolean => {
    if (val === undefined || val === null || val === '') return true;

    if (Array.isArray(val) && val.length === 0) return true;

    if (typeof val === 'object' && !Array.isArray(val)) {
      // Single time: {hours, minutes} — require BOTH hours and minutes
      if ('hours' in val && 'minutes' in val && !('from' in val)) {
        return !val.hours || !val.minutes;
      }

      // Date range: {from: string, to: string}
      // Time range: {from: {hours, minutes}, to: {hours, minutes}}
      if ('from' in val && 'to' in val) {
        const fromEmpty =
          typeof val.from === 'object'
            ? !val.from.hours || !val.from.minutes
            : !val.from;
        const toEmpty =
          typeof val.to === 'object'
            ? !val.to.hours || !val.to.minutes
            : !val.to;
        return fromEmpty || toEmpty;
      }

      // Range question single answer: { value: number|string, image?: File }
      if ('value' in val) {
        return (
          val.value === undefined || val.value === null || val.value === ''
        );
      }
    }
    return false;
  };

  // Derive the count of answered questions (non-empty values)
  const answeredCount = Object.values(answers).filter(
    val => !isAnswerEmpty(val),
  ).length;

  // ---- Answer handling ----
  const handleAnswerChange = useCallback((questionId: string, value: any) => {
    setAnswers(prev => ({...prev, [questionId]: value}));
    // Clear error for this question once the user provides an answer
    setErrors(prev => {
      if (!prev[questionId]) return prev;
      const {[questionId]: _, ...rest} = prev;
      return rest;
    });
  }, []);

  // ---- Scroll content area to top ----
  const scrollToTop = useCallback(() => {
    scrollContainerRef.current?.scrollTo({top: 0, behavior: 'smooth'});
  }, []);

  // ---- Scroll to a specific question (used for validation errors) ----
  const scrollToQuestion = useCallback((questionId: string) => {
    const el = document.getElementById(`question-${questionId}`);
    if (el && scrollContainerRef.current) {
      el.scrollIntoView({behavior: 'smooth', block: 'center'});
    }
  }, []);

  /** Validate mandatory questions on the current page. Returns true if all OK. */
  const validateCurrentPage = useCallback((): boolean => {
    const newErrors: Record<string, string> = {};

    pageQuestions?.forEach((q: any) => {
      const val = answers[q?._id];

      // 1. Mandatory check
      if (q?.required && isAnswerEmpty(val)) {
        newErrors[q?._id] = t('forms.public.mandatoryError');
        return; // skip further checks for this question
      }

      // Skip further validation if the answer is empty (optional question)
      if (val === undefined || val === null) return;

      // 2. File upload: type, size, count vs question config
      if (q?.type === 'file-upload') {
        const cfg = q.fileUploadConfig;
        const errCode = getPublicFileUploadAnswerError(val, {
          allowedTypes: (cfg?.allowedTypes ?? []) as string[],
          maxFileSizeMB: cfg?.maxFileSizeMB ?? 10,
          maxFiles: cfg?.maxFiles ?? 1,
        });
        if (errCode) {
          const params =
            errCode === 'tooLarge'
              ? {max: cfg?.maxFileSizeMB ?? 10}
              : errCode === 'tooMany'
                ? {max: cfg?.maxFiles ?? 1}
                : {};
          newErrors[q._id] = t(
            `forms.public.fileUpload.errors.${errCode}`,
            params,
          );
          return;
        }
      }

      // 3. Partial time input check (user filled one field but not the other)
      if (q?.type === 'time') {
        if ('hours' in val && !('from' in val)) {
          // Single time — both hours and minutes needed or both empty
          const hasH = !!val.hours;
          const hasM = !!val.minutes;
          if (hasH !== hasM) {
            newErrors[q._id] = t('forms.public.incompleteTimeError');
            return;
          }
        } else if (val?.from || val?.to) {
          // Range time — each side needs both hours and minutes
          const fH = !!val.from?.hours;
          const fM = !!val.from?.minutes;
          const tH = !!val.to?.hours;
          const tM = !!val.to?.minutes;
          if (fH !== fM || tH !== tM) {
            newErrors[q._id] = t('forms.public.incompleteTimeError');
            return;
          }
        }
      }

      // 4. Date range: "from" must be before or equal to "to"
      if (
        q?.type === 'date' &&
        typeof val === 'object' &&
        val?.from &&
        val?.to
      ) {
        if (val.from > val.to) {
          newErrors[q._id] = t('forms.public.dateRangeError');
          return;
        }
      }

      // 5. Time range: "from" must be before "to"
      if (q?.type === 'time' && val?.from && val?.to) {
        const fromMins =
          Number(val.from.hours || 0) * 60 + Number(val.from.minutes || 0);
        const toMins =
          Number(val.to.hours || 0) * 60 + Number(val.to.minutes || 0);
        if (fromMins > toMins) {
          newErrors[q._id] = t('forms.public.timeRangeError');
          return;
        }
      }

      // 6. Range question (form: range) — from must be <= to
      if (
        q?.type === 'range' &&
        q?.rangeConfig?.form === 'range' &&
        typeof val === 'object' &&
        val !== null &&
        'from' in val &&
        'to' in val
      ) {
        if (Number(val.from) > Number(val.to)) {
          newErrors[q._id] = t('forms.public.rangeOrderError');
          return;
        }
        // Check dual values are within the question's configured bounds
        const qMin = q.rangeConfig?.min;
        const qMax = q.rangeConfig?.max;
        if (qMin !== undefined && Number(val.from) < Number(qMin)) {
          newErrors[q._id] = t('forms.public.rangeOutOfBoundsError');
          return;
        }
        if (qMax !== undefined && Number(val.to) > Number(qMax)) {
          newErrors[q._id] = t('forms.public.rangeOutOfBoundsError');
          return;
        }
      }

      // 7. Range question (single) — value must be within the question's bounds
      if (
        q?.type === 'range' &&
        q?.rangeConfig?.form !== 'range' &&
        q?.rangeConfig?.rangeType !== 'textual' &&
        typeof val === 'object' &&
        val !== null &&
        'value' in val
      ) {
        const qMin = q.rangeConfig?.min;
        const qMax = q.rangeConfig?.max;
        const num = Number(val.value);
        if (qMin !== undefined && num < Number(qMin)) {
          newErrors[q._id] = t('forms.public.rangeOutOfBoundsError');
          return;
        }
        if (qMax !== undefined && num > Number(qMax)) {
          newErrors[q._id] = t('forms.public.rangeOutOfBoundsError');
          return;
        }
      }
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(prev => ({...prev, ...newErrors}));
      // Scroll to the first question that has an error
      const firstErrorId = pageQuestions?.find(
        (q: any) => newErrors[q?._id],
      )?._id;
      if (firstErrorId) {
        scrollToQuestion(firstErrorId);
      }
      return false;
    }

    return true;
  }, [pageQuestions, answers, t, scrollToQuestion]);

  // ---- Navigation handlers ----
  const handleNext = useCallback(() => {
    if (!isLastPage && validateCurrentPage()) {
      setCurrentPage(prev => prev + 1);
      scrollToTop();
    }
  }, [isLastPage, validateCurrentPage, scrollToTop]);

  const handlePrev = useCallback(() => {
    if (!isFirstPage) {
      setCurrentPage(prev => prev - 1);
      scrollToTop();
    }
  }, [isFirstPage, scrollToTop]);

  const handleSubmit = async () => {
    if (createFormResponseMutation.isPending || !formId) return;
    if (!validateCurrentPage()) return;

    try {
      setIsSubmitting(true);
      const resolvedAnswers: Record<string, any> = {...answers};
      for (const q of questions) {
        if (q.type !== 'file-upload' && q.type !== 'range') continue;

        if (q.type === 'file-upload') {
          const raw = answers[q._id];
          if (!Array.isArray(raw) || raw.length === 0) continue;
          if (!(raw[0] instanceof File)) continue;
          try {
            const uploaded = await uploadFiles(raw as File[]);
            resolvedAnswers[q._id] = uploaded;
          } catch (e) {
            console.error('Public form file upload failed:', e);
            showSnackbar({
              message: t('forms.public.fileUpload.errors.uploadFailed'),
              type: 'error',
            });
            setIsSubmitting(false);
            return;
          }
        }

        if (q.type === 'range') {
          const raw = answers[q._id];
          if (!raw || !(raw.image instanceof File)) continue;
          try {
            const [uploaded] = await uploadFiles([raw.image]);
            resolvedAnswers[q._id] = {...raw, image: uploaded?.url ?? null};
          } catch (e) {
            console.error('Range image upload failed:', e);
            showSnackbar({
              message: t('forms.public.fileUpload.errors.uploadFailed'),
              type: 'error',
            });
            setIsSubmitting(false);
            return;
          }
        }
      }
      const basePayload = buildPublicFormPayload(questions, resolvedAnswers);
      const hasResponder = responderInfo.userId || responderInfo.email;
      const payload = {
        ...basePayload,
        ...(hasResponder
          ? {
              responder: {
                userId: responderInfo.userId,
                email: responderInfo.email,
                name: `${responderInfo.firstName ?? ''} ${responderInfo.lastName ?? ''}`.trim(),
              },
            }
          : {}),
      };
      await createFormResponseMutation.mutateAsync({formId, payload});
      setIsSubmitted(true);
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClearForm = () => {
    setAnswers({});
    setErrors({});
    setCurrentPage(0);
    scrollToTop();
  };

  if (!formData) {
    return (
      <div className="w-full h-screen flex items-center justify-center bg-grayLightBg">
        <p className="text-[18px] text-gray">{t('forms.public.notFound')}</p>
      </div>
    );
  }

  if (isSubmitted) {
    return (
      <div className="fixed inset-0 flex flex-col bg-grayLightBg overflow-hidden">
        {/* Header — Client logo + Cheq-Qit logo */}
        <PublicFormHeader clientLogo={formData?.clients?.[0]?.logo?.url} />

        {/* Success Screen */}
        <div className="flex-1 flex items-center justify-center px-4">
          <div className="w-full max-w-[600px] bg-white rounded-lg shadow-md border border-gray90 p-8 text-center">
            {/* Green checkmark */}
            <div className="w-16 h-16 mx-auto mb-5 rounded-full bg-green-100 flex items-center justify-center">
              <img
                src={SVG.tickGreenIcon}
                alt="icon"
                className="w-full h-full object-contain"
              />
            </div>

            {/* Form title */}
            <h2 className="text-lg font-semibold text-darkerGray mb-2">
              {formData?.title}
            </h2>

            {/* Success message */}
            <h3 className="text-xl font-bold text-darkerGray mb-2">
              {formData?.confirmationMessage ||
                t('forms.public.submitted.description')}
            </h3>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 flex flex-col bg-grayLightBg overflow-hidden">
      {/* Header — Client logo + Cheq-Qit logo */}
      <PublicFormHeader clientLogo={formData?.clients?.[0]?.logo?.url} />

      {/* Form Info — Title, description, mandatory legend */}
      <PublicFormInfo
        title={formData?.title || ''}
        description={formData?.description || ''}
      />

      {/* Form Content — Shows current page of questions */}
      <PublicFormContent
        ref={scrollContainerRef}
        questions={pageQuestions}
        answers={answers}
        errors={errors}
        onAnswerChange={handleAnswerChange}
      />

      {/* Navigation Bar — Submit/Prev/Next, progress bar, clear form */}
      <PublicFormNavBar
        answeredCount={answeredCount}
        totalQuestions={questions.length}
        isFirstPage={isFirstPage}
        isLastPage={isLastPage}
        isSubmitting={isSubmitting}
        onNext={handleNext}
        onPrev={handlePrev}
        onSubmit={handleSubmit}
        onClearForm={handleClearForm}
      />
    </div>
  );
}

export default PublicForm;
